/**
 * Translation Context
 *
 * This file provides internationalization (i18n) support for the application using React Context.
 * It manages the current language state and provides translation functionality throughout the app.
 *
 * The context supports multiple languages and persists the user's language preference.
 * It includes a translation function that falls back to syllabics if a translation is not available.
 */

import React, { createContext, useState, useContext, useEffect } from "react";
import translations from "../translations/dictionary.json";
import { languageService } from "./languageService";

// Create the translation context
const TranslationContext = createContext();

/**
 * Translation Provider Component
 *
 * This component provides translation functionality to its children through React Context.
 * It manages the current language state and provides methods to change the language and translate text.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to translation context
 */
export const TranslationProvider = ({ children }) => {
  // Initialize current language from user's preference
  const [currentLanguage, setCurrentLanguage] = useState(() =>
    languageService.getPreferredLanguage()
  );

  /**
   * Handles language change and updates both state and localStorage
   * @param {string} newLanguage - The new language code to set
   */
  const handleLanguageChange = (newLanguage) => {
    setCurrentLanguage(newLanguage);
    languageService.setPreferredLanguage(newLanguage);
  };

  /**
   * Translates a given key to the current language
   * Falls back to syllabics if translation is not available
   * @param {string} key - The translation key to look up
   * @returns {string} The translated text or the key if translation not found
   */
  const translate = (key) => {
    if (!translations[key]) return key;
    return translations[key][currentLanguage] || translations[key]["syllabics"];
  };

  // Commented out font size configuration for future use
  // const getFontSize = (type) => {
  //   const sizes = {
  //     syllabics: {
  //       title: "1.1rem",
  //       body: "0.9rem",
  //       subtitle: "0.8rem",
  //       heading: "1.3rem",
  //       button: "0.85rem"
  //     },
  //     roman: {
  //       title: "1rem",
  //       body: "0.85rem",
  //       subtitle: "0.75rem",
  //       heading: "1.2rem",
  //       button: "0.8rem"
  //     },
  //     english: {
  //       title: "1.2rem",
  //       body: "1rem",
  //       subtitle: "0.9rem",
  //       heading: "1.4rem",
  //       button: "0.9rem"
  //     }
  //   };
  //   return sizes[currentLanguage]?.[type] || sizes.english[type];
  // };

  return (
    <TranslationContext.Provider
      value={{
        currentLanguage,
        setCurrentLanguage: handleLanguageChange,
        translate,
        // getFontSize
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

/**
 * Custom hook to access translation context
 * @returns {Object} Translation context values including currentLanguage, setCurrentLanguage, and translate
 */
export const useTranslation = () => useContext(TranslationContext);
